// ~/apps/themusicchurch-app/frontend/src/components/Common/Button.jsx

import React from "react";
import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const buttonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.spacing.sm};
  padding: ${(props) => {
    switch (props.size) {
      case "small":
        return `${props.theme.spacing.sm} ${props.theme.spacing.lg}`;
      case "large":
        return `${props.theme.spacing.lg} ${props.theme.spacing["3xl"]}`;
      default:
        return `${props.theme.spacing.md} ${props.theme.spacing["2xl"]}`;
    }
  }};
  font-size: ${(props) => {
    switch (props.size) {
      case "small":
        return props.theme.fontSizes.sm;
      case "large":
        return props.theme.fontSizes.lg;
      default:
        return props.theme.fontSizes.base;
    }
  }};
  font-weight: 600;
  font-family: ${(props) => props.theme.fonts.body};
  border: 2px solid transparent;
  border-radius: ${(props) => (props.rounded ? "50px" : "8px")};
  cursor: pointer;
  transition: all ${(props) => props.theme.transitions.base};
  text-decoration: none;
  white-space: nowrap;

  ${(props) => {
    switch (props.variant) {
      case "secondary":
        return css`
          background: ${props.theme.colors.secondary};
          color: white;

          &:hover:not(:disabled) {
            background: ${props.theme.colors.primary};
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadows.lg};
          }
        `;
      case "accent":
        return css`
          background: ${props.theme.colors.accent};
          color: ${props.theme.colors.dark};

          &:hover:not(:disabled) {
            background: ${props.theme.ministryPage ? props.theme.colors.homeGold : props.theme.colors.white};
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadows.lg};
          }
        `;
      case "outline":
        return css`
          background: transparent;
          color: ${props.theme.ministryPage ? props.theme.colors.homeGold : props.theme.colors.primary};
          border-color: ${props.theme.ministryPage ? props.theme.colors.homeGold : props.theme.colors.primary};

          &:hover:not(:disabled) {
            background: ${props.theme.colors.primary};
            color: white;
            transform: translateY(-2px);
          }
        `;
      case "ghost":
        return css`
          background: transparent;
          color: ${props.theme.ministryPage ? props.theme.colors.homeGold : props.theme.colors.primary};

          &:hover:not(:disabled) {
            background: ${props.theme.ministryPage ? props.theme.colors.homeSurface : props.theme.colors.gray[100]};
          }
        `;
      default: // primary
        return css`
          background: ${props.theme.colors.primary};
          color: white;

          &:hover:not(:disabled) {
            background: ${props.theme.colors.secondary};
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadows.lg};
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  ${(props) =>
    props.fullWidth &&
    css`
      width: 100%;
    `}
`;

const StyledButton = styled(motion.button)`
  ${buttonStyles}
`;

const StyledLink = styled(motion(Link))`
  ${buttonStyles}
`;

const StyledAnchor = styled(motion.a)`
  ${buttonStyles}
`;

const Button = ({
  children,
  variant = "primary",
  size = "medium",
  rounded = true,
  fullWidth = false,
  disabled = false,
  onClick,
  to,
  href,
  target,
  icon,
  iconPosition = "left",
  motionProps = {},
  ...rest
}) => {
  const content = (
    <>
      {icon && iconPosition === "left" && icon}
      {children}
      {icon && iconPosition === "right" && icon}
    </>
  );

  const defaultMotionProps = {
    whileHover: { scale: disabled ? 1 : 1.02 },
    whileTap: { scale: disabled ? 1 : 0.98 },
    ...motionProps,
  };

  // If it's an internal link
  if (to) {
    return (
      <StyledLink
        to={to}
        variant={variant}
        size={size}
        rounded={rounded}
        fullWidth={fullWidth}
        {...defaultMotionProps}
        {...rest}
      >
        {content}
      </StyledLink>
    );
  }

  // If it's an external link
  if (href) {
    return (
      <StyledAnchor
        href={href}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
        variant={variant}
        size={size}
        rounded={rounded}
        fullWidth={fullWidth}
        {...defaultMotionProps}
        {...rest}
      >
        {content}
      </StyledAnchor>
    );
  }

  // Default button
  return (
    <StyledButton
      onClick={onClick}
      disabled={disabled}
      variant={variant}
      size={size}
      rounded={rounded}
      fullWidth={fullWidth}
      {...defaultMotionProps}
      {...rest}
    >
      {content}
    </StyledButton>
  );
};

export default Button;
